import React from 'react';

import getUniqueId from 'utils/getUniqueId';
import {
	stopAllSounds,
	registerSound,
	unregisterSound,
	createHowl,
	playHowl,
	unlockAudio,
} from 'utils/playSound';

import './styles.scss';
import strings from 'strings';

const DASHARRAY_LENGTH = 230;

class AudioPlayer extends React.Component {
	constructor(props) {
		super(props);

		this.uniqueId = `audio${getUniqueId()}`;

		this.state = {
			isPlaying: false,
			duration: 0,
			currentTime: 0,
			isDone: false,
		};

		this.audio = null;
		this.isUnmounted = false;
		this.playbackRequested = false;
		this.waitingForUnlock = false;
		this.retryingLoad = false;
		this.loadRetryCount = 0;
		this.playRetryCount = 0;
		this.lastPlayFailed = false;
		this.tick = this.tick.bind(this);
	}

	componentDidMount() {
		if (this.props.autoplay) {
			this.handleStartPlayer();
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.src !== this.props.src) {
			this.cleanupAudio();

			if (this.props.playOnSourceChanged) {
				this.handleStartPlayer();
			} else {
				this.setState({isPlaying: false});
			}
		}
	}

	componentWillUnmount() {
		this.isUnmounted = true;
		cancelAnimationFrame(this.animationFrame);
		this.cleanupAudio();
	}

	cleanupAudio = () => {
		if (this.audio) {
			this.audio.off('unlock');
			unregisterSound(this.audio);
			this.audio.stop();
			this.audio.unload();
			this.audio = null;
		}
		this.playbackRequested = false;
		this.waitingForUnlock = false;
		this.retryingLoad = false;
		this.loadRetryCount = 0;
		this.playRetryCount = 0;
		this.lastPlayFailed = false;
	};

	updateProgress = () => {
		if (!this.audio) return;

		const duration = this.audio.duration();
		if (duration > 0) {
			this.setState({
				currentTime: this.audio.seek(),
				duration: duration,
			});
		}
	};

	tick() {
		if (!this.audio) return;

		if (!this.audio.playing()) {
			return;
		}

		this.updateProgress();
		this.animationFrame = requestAnimationFrame(this.tick);
	}

	handleStartPlayer = (event) => {
		const {src} = this.props;
		if (!src) return;
		const isUserInitiated = Boolean(event && event.type);

		if (isUserInitiated) {
			unlockAudio();
		}

		// Toggle: pause if already playing
		if (this.state.isPlaying) {
			this.handlePause();
			return;
		}

		if (isUserInitiated && this.audio && this.lastPlayFailed) {
			this.cleanupAudio();
		}

		// Stop all other players.
		stopAllSounds();

		this.setState({
			isDone: false,
		});

		this.playbackRequested = true;
		this.createAudioIfNeeded();
		this.startPlayback();
	};

	createAudioIfNeeded = () => {
		const {src} = this.props;
		if (this.audio || !src) return;

		this.audio = createHowl({
			src: Array.isArray(src) ? src : [src],
			html5: this.props.html5 !== false,
			preload: true,

			onload: () => {
				if (this.isUnmounted || !this.audio) return;

				const shouldStartAfterLoadRetry = this.retryingLoad;
				this.loadRetryCount = 0;
				this.retryingLoad = false;
				this.setState({
					duration: this.audio.duration(),
				});

				window.setTimeout(() => {
					if (
						this.isUnmounted ||
						!this.audio ||
						!shouldStartAfterLoadRetry ||
						!this.playbackRequested ||
						this.audio.playing()
					) {
						return;
					}
					this.startPlayback();
				}, 0);
			},

			onloaderror: () => {
				this.handleLoadError();
			},

			onplay: () => {
				if (this.isUnmounted) return;

				// Only show playing after Howler confirms audio really started.
				this.playRetryCount = 0;
				this.waitingForUnlock = false;
				this.lastPlayFailed = false;

				this.setState({
					isPlaying: true,
					isDone: false,
				});

				if (typeof this.props.onStart === 'function') {
					this.props.onStart();
				}

				cancelAnimationFrame(this.animationFrame);
				this.tick();
			},

			onplayerror: () => {
				this.handlePlayError();
			},

			onpause: () => {
				if (this.isUnmounted) return;

				this.setState({
					isPlaying: false,
				});
				this.playbackRequested = false;
				cancelAnimationFrame(this.animationFrame);
			},

			onstop: () => {
				if (this.isUnmounted) return;

				this.setState({
					isPlaying: false,
				});
				this.playbackRequested = false;
				cancelAnimationFrame(this.animationFrame);
			},

			onend: () => {
				if (this.isUnmounted) return;

				this.setState({
					isPlaying: false,
					isDone: true,
				});
				this.playbackRequested = false;
				this.completeCallback();
				unregisterSound(this.audio);
			},
		});

		registerSound(this.audio);
	};

	startPlayback = () => {
		if (!this.audio || this.isUnmounted) return;

		registerSound(this.audio);
		playHowl(this.audio);
	};

	handleLoadError = () => {
		if (this.isUnmounted) return;

		this.setState({
			isPlaying: false,
		});
		this.lastPlayFailed = true;

		if (this.loadRetryCount >= 2) return;

		this.loadRetryCount += 1;
		this.retryingLoad = true;
		window.setTimeout(() => {
			if (this.isUnmounted || !this.audio) return;
			this.audio.load();
		}, 250 * this.loadRetryCount);
	};

	handlePlayError = () => {
		if (this.isUnmounted || !this.audio) return;

		this.setState({
			isPlaying: false,
		});
		this.lastPlayFailed = true;
		cancelAnimationFrame(this.animationFrame);
		unlockAudio();

		if (this.waitingForUnlock) return;

		this.waitingForUnlock = true;
		this.audio.once('unlock', () => {
			if (this.isUnmounted || !this.audio) return;

			this.waitingForUnlock = false;
			if (this.playbackRequested && !this.audio.playing()) {
				this.startPlayback();
			}
		});

		if (this.playRetryCount >= 2) return;

		this.playRetryCount += 1;
		window.setTimeout(() => {
			if (
				this.isUnmounted ||
				!this.audio ||
				!this.playbackRequested ||
				this.audio.playing()
			) {
				return;
			}
			this.startPlayback();
		}, 300 * this.playRetryCount);
	};

	handlePause = () => {
		if (!this.audio) return;

		this.audio.pause();

		this.setState({
			isPlaying: false,
		});

		cancelAnimationFrame(this.animationFrame);
		this.playbackRequested = false;

		if (typeof this.props.onPause === 'function') {
			this.props.onPause();
		}
	};

	completeCallback() {
		if (this.props.onComplete) {
			this.props.onComplete();
		}
	}

	render() {
		const {disabled, inline} = this.props;
		const {currentTime, duration, isPlaying} = this.state;

		let progress = 0;
		if (isPlaying && duration > 0) {
			const percentageDone = (currentTime * 100) / duration;
			progress =
				DASHARRAY_LENGTH - (percentageDone * DASHARRAY_LENGTH) / 100;
		}

		let buttonClasses = 'lit-circle lit-audio';
		if (isPlaying) buttonClasses += ' is-playing';
		if (inline) buttonClasses += ' inline';

		return (
			<button
				className={buttonClasses}
				disabled={disabled}
				onClick={this.handleStartPlayer}
				title={disabled ? strings.max2Listens : null}
			>
				<svg
					className="lit-circle__interactive"
					height="90"
					viewBox="0 0 90 90"
					width="90"
				>
					<g transform="rotate(-84 45 45)">
						<circle
							className="lit-audio__outline"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							stroke="rgba(0,0,0,0.1)"
							strokeWidth="5"
						/>
						<circle
							className="lit-audio__progress"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							stroke="#fff"
							strokeWidth="6"
							style={{
								strokeDashoffset: `${progress}`,
							}}
						/>
					</g>
				</svg>
				<div className="lit-audio__icon" />
			</button>
		);
	}
}

export default AudioPlayer;
